import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loading, PageHeader, EmptyState } from "../../components/shared";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Search } from "lucide-react";

const STATUS_TONE = {
  SUBMITTED: "bg-amber-100 text-amber-800",
  ACCEPTED: "bg-emerald-100 text-emerald-800",
  DECLINED: "bg-rose-100 text-rose-800",
  CANCELLED: "bg-muted text-muted-foreground",
};
const FILTERS = ["ALL", "SUBMITTED", "ACCEPTED", "DECLINED", "CANCELLED"];

export default function AdminExpertRequests() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = (s = status, query = q) =>
    api.get("/admin/expert-requests", { params: { status: s === "ALL" ? "" : s, q: query } })
      .then((r) => setRows(r.data)).catch(() => setRows([]));

  useEffect(() => { load("ALL", ""); }, []);

  const view = async (requestId) => {
    try { setDetail((await api.get(`/admin/expert-requests/${requestId}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load request"); }
  };

  if (!rows) return <Loading />;
  return (
    <div>
      <PageHeader title="Expert Consultations" subtitle="Oversight of all society consultation requests. Read-only." />
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-request-search" className="pl-9" placeholder="Search subject, expert, service or society" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <select data-testid="admin-request-status" className="h-10 border rounded-md px-3 text-sm" value={status} onChange={(e) => { setStatus(e.target.value); load(e.target.value, q); }}>
          {FILTERS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <Button data-testid="admin-request-search-btn" onClick={() => load()} className="gap-2"><Search className="w-4 h-4" />Search</Button>
      </div>

      {rows.length === 0 ? <EmptyState icon={GraduationCap} title="No consultation requests" subtitle="Requests raised by societies will appear here." /> : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full text-sm" data-testid="admin-requests-table">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-3">Request</th><th className="p-3">Society</th><th className="p-3">Expert</th><th className="p-3">Service</th><th className="p-3">Status</th><th className="p-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.request_id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => view(r.request_id)} data-testid={`admin-request-${r.request_id}`}>
                  <td className="p-3 font-medium max-w-[220px] truncate">{r.subject}</td>
                  <td className="p-3">{r.society_name || r.society_id}</td>
                  <td className="p-3">{r.expert_snapshot?.name}</td>
                  <td className="p-3">{r.service_snapshot?.name}</td>
                  <td className="p-3"><span className={`text-xs rounded-full px-2.5 py-1 ${STATUS_TONE[r.status]}`}>{r.status}</span></td>
                  <td className="p-3 text-muted-foreground text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="admin-request-detail">
          <DialogHeader><DialogTitle className="font-display">Consultation Request</DialogTitle><DialogDescription className="sr-only">Read-only consultation request oversight</DialogDescription></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-base">{detail.subject}</span>
                <span className={`text-xs rounded-full px-2.5 py-1 ${STATUS_TONE[detail.status]}`}>{detail.status}</span>
              </div>
              <Row label="Request ID" value={detail.request_id} />
              <Row label="Society" value={detail.society_name || detail.society_id} />
              <Row label="Requester" value={detail.requester_type} />
              <Row label="Expert" value={detail.expert_snapshot?.name} />
              <Row label="Service" value={detail.service_snapshot?.name} />
              {detail.description && <Row label="Details" value={detail.description} />}
              {detail.preferred_mode && <Row label="Preferred mode" value={detail.preferred_mode.replace("_", " ")} />}
              {detail.preferred_date_time_note && <Row label="Preferred timing" value={detail.preferred_date_time_note} />}
              {detail.decline_reason && <Row label="Expert note" value={detail.decline_reason} />}
              <div>
                <p className="text-xs text-muted-foreground mb-1 mt-2">Timeline</p>
                <ul className="space-y-1">{(detail.timeline || []).map((t, i) => <li key={i} className="text-xs flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{t.status} · {new Date(t.at).toLocaleString()}</li>)}</ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex gap-2"><span className="text-muted-foreground min-w-[120px]">{label}</span><span className="font-medium break-all">{value}</span></div>;
}
